import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Clock, Video, Crown } from "lucide-react";
import { formatHoursMinutes, formatVideoDuration, formatVideoDurationInMinutes } from "@/lib/video-metadata";
import { ProcessingUsage } from "@/lib/quota/computeProcessingUsage";
import { classifyQuotaLevel } from "@/lib/quota/deriveQuotaPressure";
import { quotaLevelBarClass } from "@/lib/quota/quotaLevelBarClass";

interface ProcessingUsagePanelProps {
  /** Whether the underlying `myData` query is still loading. */
  loading: boolean;
  /** Derived usage figures from `computeProcessingUsage`. */
  usage: ProcessingUsage;
  /** Seconds this project will consume (full video, or the selected segment). */
  effectiveDurationSeconds: number;
  /** Whether a video has been picked yet (drives the "will consume" line). */
  hasVideo: boolean;
  /** Final gated decision from the caller (already accounts for mode/loading). */
  exceedsProcessingLimit: boolean;
  /**
   * When the caller is processing only a selected segment, its duration in
   * seconds — renders the "processing only the selected segment" hint. Omit
   * (or null) when the whole video is processed.
   */
  selectedSegmentDurationSeconds?: number | null;
}

/**
 * Shared monthly-usage panel for the upload modals: processing-time bar, clips
 * bar, a free-tier upgrade button, the "this video will consume X" line, and an
 * "insufficient time" warning with an upgrade CTA when the project would
 * overrun the remaining quota. Used by both the regular project upload
 * (`UploadModal`) and the news-PDF upload (`NewsPdfUploadModal`).
 */
const ProcessingUsagePanel = ({
  loading,
  usage,
  effectiveDurationSeconds,
  hasVideo,
  exceedsProcessingLimit,
  selectedSegmentDurationSeconds,
}: ProcessingUsagePanelProps) => {
  const { t } = useTranslation();

  // Tone the bars as they fill instead of staying neutral until the gate
  // fires. Paid plans keep the plain primary fill.
  const processingLevel = usage.isPaidPlan ? null : classifyQuotaLevel({
    kind: 'time',
    used: usage.currentMonthProcessingSeconds,
    max: usage.planProcessingSeconds,
  });
  const clipsLevel = usage.isPaidPlan ? null : classifyQuotaLevel({
    kind: 'countable',
    used: usage.currentMonthClips,
    max: usage.maxClips,
  });

  return (
    <div className="bg-gradient-to-br from-background/80 to-background/60 p-4 sm:p-6 rounded-xl border border-primary/20 shadow-sm space-y-4">
      {loading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-sm text-muted-foreground">{t('uploadModalExtra2.loadingUsageData')}</span>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Processing Time Usage */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-primary" />
                {t('uploadModalExtra2.processingTime')}
              </span>
              <span className="font-medium">
                {formatHoursMinutes(usage.currentMonthProcessingSeconds)} / {formatHoursMinutes(usage.planProcessingSeconds)}
              </span>
            </div>
            <Progress value={usage.usagePercentage} className="h-2" indicatorClassName={quotaLevelBarClass(processingLevel)} />
          </div>

          {/* Clips Usage */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="flex items-center gap-1">
                <Video className="w-4 h-4 text-primary" />
                {t('uploadModalExtra2.clipsCreated')}
              </span>
              <span className="font-medium">
                {usage.currentMonthClips} / {usage.maxClips}
              </span>
            </div>
            <Progress value={usage.clipsPercentage} className="h-2" indicatorClassName={quotaLevelBarClass(clipsLevel)} />
          </div>

          {/* Upgrade Button for Free Users */}
          {!usage.isPaidPlan && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
                onClick={() => (window.location.href = '/plans')}
              >
                {t('uploadModalExtra2.upgradeToPro')}
              </Button>
            </div>
          )}
        </div>
      )}

      <p className="text-xs sm:text-sm text-center text-primary font-medium">
        {t('uploadModalExtra2.videoWillConsume', { duration: hasVideo ? formatVideoDurationInMinutes(effectiveDurationSeconds) : "" })}
        {selectedSegmentDurationSeconds != null && (
          <span className="block text-xs text-muted-foreground mt-1">
            {t('uploadModalExtra2.processingSelectedSegment', { duration: formatVideoDuration(selectedSegmentDurationSeconds) })}
          </span>
        )}
      </p>

      {exceedsProcessingLimit && (
        <div className="border border-red-900 rounded-lg p-4 mt-2 space-y-3">
          <p className="text-sm text-center">
            {t('uploadModalExtra2.insufficientTime', { required: formatVideoDurationInMinutes(effectiveDurationSeconds), available: formatVideoDurationInMinutes(usage.remainingProcessingSeconds) })}
            <br />
            {t('uploadModalExtra2.considerUpgrading')}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              variant="gradient"
              size="lg"
              className="text-md"
              onClick={() => (window.location.href = '/plans')}
            >
              <Crown className="w-3 h-3 mr-1" />
              {t('uploadModalExtra2.upgradePlan')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessingUsagePanel;
